import React, { useState, useEffect } from "react";
import { Modal, Button, Card, Form, Row, Col, Accordion, Badge } from "react-bootstrap";
import { api } from "../utils/api";
import { UserContext } from "../utils/UserContext";
import smallLogo from "../media/small_logo.svg";
import Rating from "react-rating";
import getInitials from "../utils/getInitials";

const MealCard = ({ meal, setMeal, onAdd }) => {
  const userContext = React.useContext(UserContext);
  const [reviews, setReviews] = useState(null);
  const [users, setUsers] = useState(null);
  const [rating, setRating] = useState(null);
  const [userHasRevieved, setUserHasRevieved] = useState(null);
  const [form, setForm] = useState({
    rating: null,
    text: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!meal) return;
    if (userContext.access.reviews !== true) return;
    api
      .getReviewsByProduct(meal.name)
      .then(({ reviews, rating, users, userHasRevieved }) => {
        setRating(rating);
        setUsers(users);
        setReviews(reviews);
        setUserHasRevieved(userHasRevieved);
      });
  }, [meal]);

  const onAddReview = () => {
    setIsLoading(true);
    api
      .createReview({
        product: meal.name,
        text: form.text,
        rating: form.rating,
      })
      .then(() => {
        api
          .getReviewsByProduct(meal.name)
          .then(({ reviews, rating }) => {
            setForm({ rating: null, text: "" });
            setUserHasRevieved(true);
            setRating(rating);
            setReviews(reviews);
          })
          .finally(() => setIsLoading(false));
      });
  };

  const onEditReview = () => {
    setIsLoading(true);
    api
      .editReview({
        _id: form._id,
        text: form.text,
        rating: form.rating,
      })
      .then(() => {
        api
          .getReviewsByProduct(meal.name)
          .then(({ reviews, rating }) => {
            setForm({ rating: null, text: "" });
            setRating(rating);
            setReviews(reviews);
          })
          .finally(() => setIsLoading(false));
      });
  };

  const onDeleteReview = (id) => {
    setIsLoading(true);
    api.deleteReview(id).then(() => {
      api
        .getReviewsByProduct(meal.name)
        .then(({ reviews, rating }) => {
          setRating(rating);
          setReviews(reviews);
          setUserHasRevieved(false);
        })
        .finally(() => setIsLoading(false));
    });
  };

  return (
    <Modal
      show={!!meal}
      onHide={() => setMeal(null)}
      size="lg"
      fullscreen="md"
      centered
      dialogClassName="order-modal"
    >
      <Modal.Header closeButton>
        <div className="d-flex flex-column w-100 gap-1">
          <Modal.Title className="fw-bold m-0">{meal?.name}</Modal.Title>
          <p className="m-0 text-muted small">{meal?.subName}</p>
        </div>
      </Modal.Header>
      <Modal.Body className="p-3">
        {meal ? (
          <div className="d-flex flex-column gap-3">
            <div className="glass-panel p-0 overflow-hidden">
              <div
                className="position-relative"
                style={{
                  background: "linear-gradient(135deg, rgba(108,175,233,0.18), rgba(95,209,255,0.18))",
                }}
              >
                <div className="p-3 d-flex flex-column flex-md-row gap-3 align-items-stretch">
                  <div
                    className="ratio ratio-1x1 rounded"
                    style={{
                      maxWidth: 260,
                      width: "100%",
                      background: "#f3f6ff",
                      border: "1px solid rgba(0,0,0,0.04)",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={meal.image ? meal.image : smallLogo}
                      alt={meal.name}
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                  </div>
                  <div className="flex-grow-1 d-flex flex-column gap-2 w-100 justify-content-between">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {rating ? (
                        <>
                          <Rating
                            emptySymbol="bi bi-star text-primary fs-3"
                            fullSymbol="bi bi-star-fill text-primary fs-3"
                            stop={10}
                            readonly={true}
                            className="ms-1"
                            initialRating={rating}
                          />
                          <span className="fw-semibold">{rating.toFixed(1)}</span>
                        </>
                      ) : (
                        ""
                      )}
                      <span className="badge bg-light text-dark">{meal.weight} {meal.weightUnit}</span>
                    </div>
                    {meal.description ? (
                      <div className="text-muted">{meal.description}</div>
                    ) : (
                      ""
                    )}
                    <div className="d-flex gap-2 flex-wrap">
                      {meal.calories ? (
                        <span className="badge bg-primary text-light">Ккал: {meal.calories}</span>
                      ) : (
                        ""
                      )}
                      {meal.proteins ? (
                        <span className="badge bg-primary-subtle text-primary">Б: {meal.proteins}</span>
                      ) : (
                        ""
                      )}
                      {meal.fats ? (
                        <span className="badge bg-primary-subtle text-primary">Ж: {meal.fats}</span>
                      ) : (
                        ""
                      )}
                      {meal.carbohydrates ? (
                        <span className="badge bg-primary-subtle text-primary">У: {meal.carbohydrates}</span>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2">
                      <div className="price-hero">
                        <span className="price-hero__value">{meal.price} ₽</span>
                        <span className="price-hero__label">за порцию</span>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="primary"
                          onClick={() => {
                            if (onAdd) {
                              onAdd({ name: meal.name, price: meal.price, mealId: meal._id });
                              setMeal(null);
                            }
                          }}
                        >
                          <i className="bi bi-cart-plus me-1" />
                          {meal.price} ₽
                        </Button>
                        <Button variant="outline-primary" onClick={() => setMeal(null)}>Закрыть</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Accordion className="mb-2" defaultActiveKey={["1", "2"]} alwaysOpen>
              {meal.description ? (
                <Accordion.Item eventKey="1">
                  <Accordion.Header>Описание</Accordion.Header>
                  <Accordion.Body>
                    <p>{meal.description}</p>
                  </Accordion.Body>
                </Accordion.Item>
              ) : (
                ""
              )}
              <Accordion.Item eventKey="2">
                <Accordion.Header>КБЖУ</Accordion.Header>
                <Accordion.Body>
                  <div className="nutri-header glass-panel mb-3 p-3">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="nutri-chip">
                        <span className="text-muted">Ккал</span>
                        <span className="fw-bold fs-5">{meal?.calories ?? "—"}</span>
                      </div>
                      <span className="badge bg-light text-dark">
                        {meal.weight && meal.weightUnit
                          ? `${meal.weight} ${meal.weightUnit}`
                          : "Порция"}
                      </span>
                      <span className="text-muted small">Данные из карточки блюда</span>
                    </div>
                  </div>
                  <div className="nutri-grid">
                    <div className="nutri-card">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="nutri-label">Белки</span>
                        <span className="nutri-value">{meal?.proteins ?? "—"}</span>
                      </div>
                      <div className="nutri-bar">
                        <span className="fill proteins" />
                      </div>
                    </div>
                    <div className="nutri-card">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="nutri-label">Жиры</span>
                        <span className="nutri-value">{meal?.fats ?? "—"}</span>
                      </div>
                      <div className="nutri-bar">
                        <span className="fill fats" />
                      </div>
                    </div>
                    <div className="nutri-card">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="nutri-label">Углеводы</span>
                        <span className="nutri-value">{meal?.carbohydrates ?? "—"}</span>
                      </div>
                      <div className="nutri-bar">
                        <span className="fill carbs" />
                      </div>
                    </div>
                  </div>
                  <p className="text-muted small mt-2 mb-0">
                    Если блюдо в граммах, значения указаны за 100 г; для порций — за блюдо.
                  </p>
                </Accordion.Body>
              </Accordion.Item>
              {meal.products ? (
                <Accordion.Item eventKey="3">
                  <Accordion.Header>Состав</Accordion.Header>
                  <Accordion.Body>
                    <div className="fs-6" style={{ maxHeight: 200, overflow: "auto" }}>
                      {meal.products}
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ) : (
                ""
              )}
              {userContext.access.reviews ? (
                <Accordion.Item eventKey="4">
                  <Accordion.Header>Отзывы</Accordion.Header>
                  <Accordion.Body>
                    {reviews
                      ? reviews.map((el) => {
                          const reviewUser = users.find((le) => le._id === el.user) || {};
                          const avatarSrc = reviewUser.avatar;
                          const initials = getInitials(reviewUser.name);

                          return (
                            <Card className="p-3 mb-3" key={el._id}>
                              {userContext._id === el.user ? (
                                <div className="position-absolute top-0 end-0 m-3">
                                  <i
                                    className="bi bi-pencil text-secondary  fs-5 me-2"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setForm(el)}
                                  />
                                  <i
                                    className="bi bi-x-lg text-secondary fs-5"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => onDeleteReview(el._id)}
                                  />
                                </div>
                              ) : (
                                ""
                              )}
                              <div className="d-flex align-items-center">
                                {avatarSrc ? (
                                  <img
                                    src={avatarSrc}
                                    alt={reviewUser.name}
                                    style={{
                                      width: 48,
                                      height: 48,
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: 12,
                                      background: "linear-gradient(135deg, #6cafe9, #5fd1ff)",
                                      color: "#ffffff",
                                      fontWeight: 700,
                                      fontSize: 22,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {initials || "?"}
                                  </div>
                                )}
                                <div className="ms-3">
                                  <p className="m-0 fw-semibold">{reviewUser.name}</p>
                                  <Rating
                                    emptySymbol="bi bi-star text-primary"
                                    fullSymbol="bi bi-star-fill text-primary"
                                    stop={10}
                                    readonly={true}
                                    initialRating={el.rating}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                              <p className="mt-2 mb-0">{el.text}</p>
                            </Card>
                          );
                        })
                      : ""}
                    {userHasRevieved !== true || form._id ? (
                      <Card className="mt-4">
                        <Card.Header>{form._id ? "Изменить отзыв" : "Новый отзыв"}</Card.Header>
                        <Card.Body>
                          <Rating
                            emptySymbol="bi bi-star text-primary fs-3"
                            fullSymbol="bi bi-star-fill text-primary fs-3"
                            stop={10}
                            onChange={(r) => setForm({ ...form, rating: r })}
                            initialRating={form.rating}
                          />
                          <Form.Control
                            type="text"
                            as="textarea"
                            placeholder="Напишите свой отзыв здесь"
                            value={form.text}
                            onChange={(e) => setForm({ ...form, text: e.target.value })}
                            name="text"
                            rows={3}
                            className="mt-2"
                          />
                        </Card.Body>
                        <Card.Footer className="d-flex justify-content-end">
                          {form._id ? (
                            <Button
                              variant="primary"
                              onClick={() => onEditReview()}
                              disabled={isLoading || form.text === "" || form.rating === null}
                            >
                              {isLoading ? "Изменяем..." : "Изменить"}
                            </Button>
                          ) : (
                            <Button
                              variant="primary"
                              onClick={() => onAddReview()}
                              disabled={isLoading || form.text === "" || form.rating === null}
                            >
                              {isLoading ? "Сохраняем..." : "Сохранить"}
                            </Button>
                          )}
                        </Card.Footer>
                      </Card>
                    ) : (
                      ""
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ) : (
                ""
              )}
            </Accordion>
          </div>
        ) : (
          ""
        )}
      </Modal.Body>
    </Modal>
  );
};

export default MealCard;
